import { Formik, Form } from "formik";
import { useMemo, useState } from "react";
import { Modal } from "react-bootstrap";
import * as yup from "yup";
import GradientButton from "../../../../Core/GradientButton";
import AlertModal from "../../../../Core/AlertModal";
import PinCodeBoxes from "../../../../Core/PinCodeBoxes";
import { useSessionStore } from "../../../../../Stores/SessionStore";
import { customerHasOwnPin } from "../../../customerPinAuth";

function buildValidationSchema(hasPin) {
	return yup
		.object({
			CurrentPin: hasPin
				? yup.string().required("Current PIN is required.").matches(/^\d{4}$/, "Current PIN must be exactly 4 digits.")
				: yup.string().notRequired(),
			NewPin: yup.string().required("New PIN is required.").matches(/^\d{4}$/, "New PIN must be exactly 4 digits."),
			ConfirmNewPin: yup
				.string()
				.required("Please confirm your new PIN.")
				.oneOf([yup.ref("NewPin")], "PIN confirmation should match new PIN."),
		})
		.required();
}

export default function ChangePinModal({ show, onClose }) {
	const store = useSessionStore();
	const customer = store.CustomerDetails?.Customer;
	const hasPin = customerHasOwnPin(customer);
	const validationSchema = useMemo(() => buildValidationSchema(hasPin), [hasPin]);

	const [saving, setSaving] = useState(false);
	const [error, setError] = useState(null);
	const [alert, setAlert] = useState(null);

	async function handleSubmit(values) {
		if (!customer) return;

		setError(null);
		setSaving(true);
		const result = await store.CustomersService.UpdateProfile({
			CellphoneNumber: customer.CellphoneNumber,
			Email: customer.Email,
			Language: customer.LanguagePreference,
			CommunicationPreference: customer.EmailPreference,
			CurrentPin: hasPin ? values.CurrentPin : undefined,
			NewPin: values.NewPin,
			NewPinConfirm: values.ConfirmNewPin,
		});
		setSaving(false);

		if (result?.Success) {
			await store.LoadCustomerDetails();
			setAlert({
				show: true,
				success: true,
				title: hasPin ? "PIN Updated" : "PIN Set",
				message: hasPin ? "Your PIN has been updated successfully." : "Your PIN has been set successfully.",
				onClose: () => {
					setAlert(null);
					onClose();
				},
			});
			return;
		}

		setError(result?.Message ?? "Failed to update your PIN. Please try again.");
	}

	return (
		<>
			<Modal show={show} onHide={onClose} size="md">
				<Modal.Header>
					<h3>{hasPin ? "Change PIN" : "Set PIN"}</h3>
				</Modal.Header>
				<Modal.Body>
					<Formik
						initialValues={{ CurrentPin: "", NewPin: "", ConfirmNewPin: "" }}
						validationSchema={validationSchema}
						onSubmit={handleSubmit}
						enableReinitialize
					>
						{({ handleSubmit: submit, values, setFieldValue, errors, touched, isValid, dirty }) => (
							<Form>
								{hasPin && (
									<div className="mb-4 text-center">
										<label className="form-label mb-3">Current PIN</label>
										<PinCodeBoxes value={values.CurrentPin} onChange={(value) => setFieldValue("CurrentPin", value)} />
										<small className="text-danger">{touched.CurrentPin && errors.CurrentPin}</small>
									</div>
								)}
								<div className="mb-4 text-center">
									<label className="form-label mb-3">New PIN</label>
									<PinCodeBoxes value={values.NewPin} onChange={(value) => setFieldValue("NewPin", value)} />
									<small className="text-danger">{touched.NewPin && errors.NewPin}</small>
								</div>
								<div className="mb-4 text-center">
									<label className="form-label mb-3">Confirm New PIN</label>
									<PinCodeBoxes value={values.ConfirmNewPin} onChange={(value) => setFieldValue("ConfirmNewPin", value)} />
									<small className="text-danger">{touched.ConfirmNewPin && errors.ConfirmNewPin}</small>
								</div>
								{error && <small className="text-danger d-block mb-3">{error}</small>}
								<GradientButton
									title={saving ? "Saving..." : "Save Changes"}
									variant="greenYellow"
									onClick={() => submit()}
									disabled={saving || !isValid || !dirty}
									loading={saving}
								/>
								<GradientButton title="Cancel" variant="yellowOrange" onClick={onClose} disabled={saving} className="mt-2" />
							</Form>
						)}
					</Formik>
				</Modal.Body>
			</Modal>
			{alert && <AlertModal modal={alert} />}
		</>
	);
}
